import * as React from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { useAppSelector } from "@/store/hooks";
import {
  selectHasGivenGdprConsent,
  selectHasSeenEntranceAnimation,
} from "@/store/slices/onboardingSlice";
import { Button } from "@/ui/components/ui/button";
import { Input } from "@/ui/components/ui/input";
import { SocialLoginButton } from "@/ui/components/ui/social-login-button";
import { ConsentBanner } from "@/ui/components/ConsentBanner";
import { EyeIcon, EyeOffIcon } from "@/ui/components/icons";
import { PasswordStrengthIndicator } from "./PasswordStrengthIndicator";

export interface SignupFormProps {
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Callback when Google sign-in is clicked
   */
  onGoogleSignIn?: () => void;
  /**
   * Callback when Apple sign-in is clicked
   */
  onAppleSignIn?: () => void;
  /**
   * Callback when the sign-up button is clicked
   */
  onSignUp?: (data: {
    email: string;
    password: string;
    confirmPassword: string;
    username: string;
    gdprConsent: boolean;
  }) => void;
  /**
   * Callback when "Sign in" link is clicked
   */
  onSignIn?: () => void;
  /**
   * Loading state for Google sign-in
   */
  googleLoading?: boolean;
  /**
   * Loading state for Apple sign-in
   */
  appleLoading?: boolean;
  /**
   * Loading state for email/password sign-up
   */
  signUpLoading?: boolean;
  /**
   * Global error message
   */
  error?: string | null;
  /**
   * Field-specific errors
   */
  fieldErrors?: Record<
    "email" | "password" | "confirmPassword" | "username" | "gdprConsent",
    string
  >;
}

/**
 * SignupForm Component
 *
 * A complete signup form with:
 * - Social login options (Google and Apple) with loading states
 * - Email, username, password, and confirm password inputs
 * - Password visibility toggle
 * - Unified GDPR consent checkbox (single checkbox for Terms, Privacy, Marketing)
 * - Sign up button with loading state
 * - Sign in link
 *
 * Mirrors LoginForm structure for consistency.
 *
 * @example
 * ```tsx
 * <SignupForm
 *   onGoogleSignIn={() => console.log('Google sign in')}
 *   onAppleSignIn={() => console.log('Apple sign in')}
 *   onSignUp={(data) => console.log('Sign up', data)}
 *   onSignIn={() => console.log('Navigate to sign in')}
 *   googleLoading={false}
 *   appleLoading={false}
 *   signUpLoading={false}
 * />
 * ```
 */
export const SignupForm = React.forwardRef<HTMLDivElement, SignupFormProps>(
  (
    {
      className,
      onGoogleSignIn,
      onAppleSignIn,
      onSignUp,
      onSignIn,
      googleLoading = false,
      appleLoading = false,
      signUpLoading = false,
      error = null,
      fieldErrors,
    },
    ref
  ) => {
    const { t } = useTranslation("auth");

    // Get persisted consent state from onboarding flow
    const persistedConsent = useAppSelector(selectHasGivenGdprConsent);
    const hasSeenAnimation = useAppSelector(selectHasSeenEntranceAnimation);

    // If user consented during entrance animation, pre-check the consent
    const [email, setEmail] = React.useState("");
    const [password, setPassword] = React.useState("");
    const [confirmPassword, setConfirmPassword] = React.useState("");
    const [username, setUsername] = React.useState("");
    const [gdprConsent, setGdprConsent] = React.useState(persistedConsent);
    const [showPassword, setShowPassword] = React.useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);

    // Hide consent banner if user came from entrance animation and already consented
    const shouldShowConsentBanner = !(hasSeenAnimation && persistedConsent);

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      if (!signUpLoading) {
        onSignUp?.({
          email,
          password,
          confirmPassword,
          username,
          gdprConsent,
        });
      }
    };

    const togglePasswordVisibility = () => {
      setShowPassword((prev) => !prev);
    };

    const toggleConfirmPasswordVisibility = () => {
      setShowConfirmPassword((prev) => !prev);
    };

    const isAnyLoading = googleLoading || appleLoading || signUpLoading;
    const isSubmitDisabled = isAnyLoading || !gdprConsent;

    return (
      <div
        ref={ref}
        className={cn(
          "bg-white box-border flex flex-col gap-4 items-center px-4 py-2 rounded-[0.3125rem] w-full",
          className
        )}
      >
        {/* Title */}
        <h2 className="font-montserrat font-semibold text-gray-900 text-xl leading-7 w-full">
          {t("signup.title")}
        </h2>

        {/* Subtitle */}
        <p className="font-montserrat font-normal text-[0.9375rem] text-gray-700 tracking-[0.3px] leading-5 w-full">
          {t("signup.subtitle")}
        </p>

        {/* Global Error Banner */}
        {error && (
          <div
            role="alert"
            className="w-full bg-red-100 border border-red-200 text-brand-red rounded-[0.3125rem] px-4 py-3 text-sm"
          >
            {error}
          </div>
        )}

        {/* Social Signup Buttons */}
        <SocialLoginButton
          variant="google"
          onClick={onGoogleSignIn}
          className="w-full"
          aria-label={t("signup.socialGoogle")}
          disabled={isAnyLoading || !gdprConsent}
        >
          {googleLoading ? t("loading.signingUp") : t("signup.socialGoogle")}
        </SocialLoginButton>

        <SocialLoginButton
          variant="apple"
          onClick={onAppleSignIn}
          className="w-full"
          aria-label={t("signup.socialApple")}
          disabled={isAnyLoading || !gdprConsent}
        >
          {appleLoading ? t("loading.signingUp") : t("signup.socialApple")}
        </SocialLoginButton>

        {/* Divider Text */}
        <p className="font-montserrat font-normal text-[0.9375rem] text-gray-700 text-center tracking-[0.3px] leading-5">
          {t("login.divider")}
        </p>

        {/* Signup Form */}
        <form onSubmit={handleSubmit} className="flex flex-col gap-4 w-full">
          {/* Email Input */}
          <div className="flex flex-col gap-1 w-full">
            <Input
              type="email"
              variant="anyw3ar"
              placeholder={t("signup.form.email")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              aria-label={t("signup.form.email")}
              aria-invalid={fieldErrors?.email ? "true" : "false"}
              disabled={signUpLoading}
              className={cn(
                fieldErrors?.email && "border-brand-red focus:ring-brand-red"
              )}
            />
            {fieldErrors?.email && (
              <span className="text-brand-red text-sm" role="alert">
                {fieldErrors.email}
              </span>
            )}
          </div>

          {/* Username Input */}
          <div className="flex flex-col gap-1 w-full">
            <Input
              type="text"
              variant="anyw3ar"
              placeholder={t("signup.form.username")}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              aria-label={t("signup.form.username")}
              aria-invalid={fieldErrors?.username ? "true" : "false"}
              disabled={signUpLoading}
              className={cn(
                fieldErrors?.username &&
                  "border-brand-red focus:ring-brand-red"
              )}
            />
            {fieldErrors?.username && (
              <span className="text-brand-red text-sm" role="alert">
                {fieldErrors.username}
              </span>
            )}
          </div>

          {/* Password Input with visibility toggle */}
          <div className="flex flex-col gap-1 w-full">
            <div className="relative w-full">
              <Input
                type={showPassword ? "text" : "password"}
                variant="anyw3ar"
                placeholder={t("signup.form.password")}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                aria-label={t("signup.form.password")}
                aria-invalid={fieldErrors?.password ? "true" : "false"}
                disabled={signUpLoading}
                className={cn(
                  "pr-12",
                  fieldErrors?.password &&
                    "border-brand-red focus:ring-brand-red"
                )}
              />
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-gray-500 hover:text-gray-700 focus:outline-none focus:text-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label={showPassword ? "Hide password" : "Show password"}
                disabled={signUpLoading}
              >
                {showPassword ? (
                  <EyeOffIcon size={16} />
                ) : (
                  <EyeIcon size={16} />
                )}
              </button>
            </div>
            {fieldErrors?.password && (
              <span className="text-brand-red text-sm" role="alert">
                {fieldErrors.password}
              </span>
            )}

            {/* Password Strength Indicator */}
            <PasswordStrengthIndicator password={password} />
          </div>

          {/* Confirm Password Input with visibility toggle */}
          <div className="flex flex-col gap-1 w-full">
            <div className="relative w-full">
              <Input
                type={showConfirmPassword ? "text" : "password"}
                variant="anyw3ar"
                placeholder={t("signup.form.confirmPassword")}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                aria-label={t("signup.form.confirmPassword")}
                aria-invalid={fieldErrors?.confirmPassword ? "true" : "false"}
                disabled={signUpLoading}
                className={cn(
                  "pr-12",
                  fieldErrors?.confirmPassword &&
                    "border-brand-red focus:ring-brand-red"
                )}
              />
              <button
                type="button"
                onClick={toggleConfirmPasswordVisibility}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-gray-500 hover:text-gray-700 focus:outline-none focus:text-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label={
                  showConfirmPassword
                    ? "Hide confirm password"
                    : "Show confirm password"
                }
                disabled={signUpLoading}
              >
                {showConfirmPassword ? (
                  <EyeOffIcon size={16} />
                ) : (
                  <EyeIcon size={16} />
                )}
              </button>
            </div>
            {fieldErrors?.confirmPassword && (
              <span className="text-brand-red text-sm" role="alert">
                {fieldErrors.confirmPassword}
              </span>
            )}
          </div>

          {/* GDPR Consent Banner - Hidden if already consented during entrance animation */}
          {shouldShowConsentBanner && (
            <div className="flex flex-col gap-2 w-full">
              <ConsentBanner
                checked={gdprConsent}
                onAccept={(consent) => setGdprConsent(consent)}
                showButton={false}
                termsUrl="https://anyw3ar.com/terms"
                privacyUrl="https://anyw3ar.com/privacy"
                cookiesUrl="https://anyw3ar.com/marketing"
              />
              {fieldErrors?.gdprConsent && (
                <span className="text-brand-red text-sm" role="alert">
                  {fieldErrors.gdprConsent}
                </span>
              )}
            </div>
          )}

          {/* Sign Up Button */}
          <Button
            type="submit"
            variant="primary-red"
            size="primary-red"
            className="w-full"
            disabled={isSubmitDisabled}
          >
            {signUpLoading ? t("loading.signingUp") : t("signup.form.submit")}
          </Button>
        </form>

        {/* Divider Line */}
        <div className="bg-gray-100 h-[0.03125rem] w-full" />

        {/* Sign In Link */}
        <div className="flex gap-2 items-start font-montserrat font-normal text-xs tracking-[0.3px] leading-5">
          <span className="text-gray-900">{t("signup.links.alreadyHaveAccount")}</span>
          <button
            type="button"
            onClick={onSignIn}
            className="text-brand-red text-right hover:underline focus:outline-none focus:underline disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isAnyLoading}
          >
            {t("signup.links.signIn")}
          </button>
        </div>
      </div>
    );
  }
);

SignupForm.displayName = "SignupForm";
